/**
 * Exit / Square-off dialog (spec Part 13-20) — Preview -> explicit
 * confirmation -> submit, exactly like Phase 4's own Execution Preview/
 * Confirm pattern. Opening this dialog only calls
 * `positionService.previewExit` (read-only). [Confirm Exit] is the ONLY
 * control that calls `positionService.confirmExit`, which is the only
 * method in this file that can place a real (reversing) broker order —
 * and only once the trader has explicitly checked the acknowledgement box.
 */
import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import positionService from "@/services/positionService";
import type { ExitPreviewRead, ExitResultRead } from "@/types";
import { extractErrorMessage } from "@/utils/errors";
import { fmtCurrency } from "@/pages/strategy-adjustment/constants";

export function ExitDialog({
  executionId,
  open,
  onClose,
  onExited,
}: {
  executionId: string | null;
  open: boolean;
  onClose: () => void;
  onExited: (result: ExitResultRead) => void;
}) {
  const [preview, setPreview] = useState<ExitPreviewRead | null>(null);
  const [quantity, setQuantity] = useState<string>("");
  const [acknowledged, setAcknowledged] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !executionId) return;
    setError(null);
    setAcknowledged(false);
    setLoading(true);
    positionService
      .previewExit(executionId, {})
      .then(setPreview)
      .catch((err) => setError(extractErrorMessage(err)))
      .finally(() => setLoading(false));
  }, [open, executionId]);

  const handleClose = () => {
    setPreview(null);
    setError(null);
    onClose();
  };

  const handleRefreshWithQuantity = async () => {
    if (!executionId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await positionService.previewExit(executionId, { quantity: quantity ? Number(quantity) : null });
      setPreview(result);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!executionId || !preview) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await positionService.confirmExit(executionId, { exit_token: preview.exit_token, acknowledged: true });
      onExited(result);
      handleClose();
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Exit Strategy</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {loading && <Typography variant="body2">Retrieving the current broker position…</Typography>}
        {preview && (
          <Stack spacing={2}>
            <Stack direction="row" spacing={3}>
              <Stack>
                <Typography variant="caption" color="text.secondary">Broker</Typography>
                <Typography variant="body2">{preview.broker}</Typography>
              </Stack>
              <Stack>
                <Typography variant="caption" color="text.secondary">Strategy</Typography>
                <Typography variant="body2">{preview.strategy_key}</Typography>
              </Stack>
              <Stack>
                <Typography variant="caption" color="text.secondary">Current P&L</Typography>
                <Typography variant="body2">{fmtCurrency(preview.current_pnl)}</Typography>
              </Stack>
            </Stack>
            <Stack direction="row" spacing={3}>
              <Stack>
                <Typography variant="caption" color="text.secondary">Open Quantity (lots)</Typography>
                <Typography variant="body2">{preview.open_quantity}</Typography>
              </Stack>
              <Stack>
                <Typography variant="caption" color="text.secondary">Estimated Exit Value</Typography>
                <Typography variant="body2">{fmtCurrency(preview.estimated_exit_value)}</Typography>
              </Stack>
              <Stack>
                <Typography variant="caption" color="text.secondary">Number of Legs</Typography>
                <Typography variant="body2">{preview.number_of_legs}</Typography>
              </Stack>
            </Stack>

            <Stack direction="row" spacing={2} alignItems="center">
              <TextField
                label="Exit Quantity (lots) — leave blank for a full exit"
                type="number"
                size="small"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
              />
              <Button size="small" onClick={handleRefreshWithQuantity}>
                Update Preview
              </Button>
            </Stack>

            <Divider />
            <Typography variant="subtitle2">Exit Orders</Typography>
            <List dense>
              {preview.legs.map((leg) => (
                <ListItem key={leg.execution_leg_id} disableGutters>
                  <ListItemText
                    primary={`${leg.exit_side} ${leg.broker_symbol ?? ""} — ${leg.exit_quantity} of ${leg.open_quantity} lots open`}
                    secondary={leg.estimated_price != null ? `Est. price ${fmtCurrency(leg.estimated_price)}` : undefined}
                  />
                </ListItem>
              ))}
            </List>

            <Alert severity="warning">{preview.warning}</Alert>

            <FormControlLabel
              control={<Checkbox checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)} />}
              label="I have reviewed the exit orders above and confirm this exit."
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button variant="contained" color="error" disabled={!preview || !acknowledged || submitting} onClick={handleConfirm}>
          Confirm Exit
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ExitDialog;
